import type { CSSProperties } from 'react'
import './LoadingState.css'

export interface SkeletonProps {
  /** CSS width, e.g. "100%", "8rem". */
  width?: string
  /** CSS height, e.g. "1rem". */
  height?: string
  /** Border radius token override. */
  radius?: string
  className?: string
}

/**
 * Content-shaped shimmer placeholder (KAN-1179). Compose several to mirror a card
 * or row while data loads.
 */
export function Skeleton({ width = '100%', height = '1rem', radius = 'var(--radius-md)', className = '' }: SkeletonProps) {
  const style: CSSProperties = { width, height, borderRadius: radius }
  return <span className={`ui-skeleton ${className}`.trim()} style={style} aria-hidden="true" />
}

export interface LoadingStateProps {
  /** Accessible loading label announced to screen readers. */
  label?: string
  /** Number of skeleton rows to render. */
  rows?: number
  className?: string
}

/**
 * Standard loading block (KAN-1179): an accessible spinner label plus a few skeleton
 * rows. Replaces bare "Loading…" text across the portals.
 */
export function LoadingState({ label = 'Loading…', rows = 3, className = '' }: LoadingStateProps) {
  return (
    <div className={`ui-loading ${className}`.trim()} role="status" aria-live="polite">
      <span className="ui-loading__spinner" aria-hidden="true" />
      <span className="ui-loading__label">{label}</span>
      <div className="ui-loading__skeletons" aria-hidden="true">
        {Array.from({ length: rows }).map((_, index) => (
          <Skeleton key={index} height="0.9rem" width={index === rows - 1 ? '60%' : '100%'} />
        ))}
      </div>
    </div>
  )
}

export default LoadingState
